import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { TrendUp, TrendDown, Minus, Crown, ArrowsLeftRight, Lightning, CaretDown, CaretUp, WifiHigh, Trophy, Check, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import PlayerDetailModal from "@/components/PlayerDetailModal";
import { PlayerPhoto } from "@/components/PlayerPhoto";
import CommissionerDashboard from "@/components/CommissionerDashboard";
import { FormatMarkdown } from "@/components/FormatMarkdown";

function PosBadge({ pos }) {
  const cls = `pos-bg-${(pos || "").toLowerCase()} pos-${(pos || "").toLowerCase()}`;
  return (
    <span className={`inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${cls}`}>
      {pos}
    </span>
  );
}

function TrendIndicator({ value }) {
  if (!value || value === 0) return <Minus size={12} className="text-[#666]" />;
  if (value > 0) return (
    <span className="flex items-center gap-0.5 text-[#4ade80] text-xs font-mono">
      <TrendUp size={12} weight="bold" />+{value}
    </span>
  );
  return (
    <span className="flex items-center gap-0.5 text-[#ff3b30] text-xs font-mono">
      <TrendDown size={12} weight="bold" />{value}
    </span>
  );
}

function ValueDisplay({ value }) {
  let cls = "text-[#a3a3a3]";
  if (value >= 7000) cls = "text-[#ccff00]";
  else if (value >= 4000) cls = "text-[#4ade80]";
  else if (value >= 2000) cls = "text-[#fbbf24]";
  return <span className={`font-mono font-bold text-sm ${cls}`}>{value.toLocaleString()}</span>;
}

export default function Dashboard({ leagueData, loading, myRosterId, setMyRosterId, user, activeLeague }) {
  const navigate = useNavigate();
  const analysisCacheKey = `dtm_analysis_${leagueData?.league?.league_id}_${myRosterId}`;
  const [rosterAnalysis, setRosterAnalysis] = useState(() => {
    try { return localStorage.getItem(analysisCacheKey) || null; } catch { return null; }
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [expandedPos, setExpandedPos] = useState(null);
  const [waiverSuggestions, setWaiverSuggestions] = useState(null);
  const [loadingWaivers, setLoadingWaivers] = useState(false);
  const [detailPlayerId, setDetailPlayerId] = useState(null);
  const [champScore, setChampScore] = useState(null);
  const [leagueScores, setLeagueScores] = useState(null);
  const [deepDivePartner, setDeepDivePartner] = useState(null);
  const [deepDiveSuggestions, setDeepDiveSuggestions] = useState(null);
  const [deepDiveLoading, setDeepDiveLoading] = useState(false);

  // Fetch Championship Score when roster is selected
  useEffect(() => {
    if (!leagueData?.rosters || !myRosterId) return;
    const roster = leagueData.rosters.find(r => r.roster_id === myRosterId);
    if (!roster?.players?.length) return;
    (async () => {
      try {
        const playerIds = roster.players.map(p => p.player_id || p);
        const res = await axios.post(`${API}/championship/score`, {
          player_ids: playerIds,
          is_superflex: leagueData.league?.settings?.is_superflex || false,
        });
        setChampScore(res.data);
      } catch (e) { console.error(e); }
    })();
  }, [leagueData, myRosterId]);

  // Fetch league-wide championship scores for enhanced power rankings
  useEffect(() => {
    if (!leagueData?.rosters?.length) return;
    (async () => {
      try {
        const rosterData = leagueData.rosters.map(r => ({
          roster_id: r.roster_id,
          players: (r.players || []).map(p => p.player_id || p),
          owner_name: r.owner_name || `Team ${r.roster_id}`,
        }));
        const res = await axios.post(`${API}/championship/league-scores`, {
          rosters: rosterData,
          is_superflex: leagueData.league?.settings?.is_superflex || false,
        });
        setLeagueScores(res.data);
      } catch (e) { console.error(e); }
    })();
  }, [leagueData]);

  if (loading) {
    return (
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="h-32 skeleton rounded-sm" />
          ))}
        </div>
      </div>
    );
  }

  if (!leagueData) return (
    <div className="max-w-[1600px] mx-auto px-6 py-16 text-center">
      <p className="text-[#a3a3a3] text-sm mb-4">Select a league to view your dashboard.</p>
    </div>
  );

  const { league, rosters, pick_values } = leagueData || {};

  if (!rosters || !rosters.length) return (
    <div className="max-w-[1600px] mx-auto px-6 py-16 text-center">
      <p className="text-[#a3a3a3] text-sm mb-4">Loading roster data...</p>
      <p className="text-[10px] text-[#555]">If this persists, try selecting the league again.</p>
    </div>
  );

  const myRoster = rosters.find(r => r.roster_id === myRosterId) || rosters[0];

  const analyzeRoster = async () => {
    if (!myRoster) return;
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/trade/roster-analysis`, {
        league_id: league.league_id,
        my_roster_id: myRosterId,
        my_roster: [myRoster],
        all_rosters: rosters,
        league_settings: league.settings,
      });
      setRosterAnalysis(res.data.analysis);
      try { localStorage.setItem(analysisCacheKey, res.data.analysis); } catch {}
    } catch (e) {
      toast.error("Failed to analyze roster");
    } finally {
      setAnalyzing(false);
    }
  };

  const playersByPos = {};
  if (myRoster) {
    for (const p of myRoster.players) {
      const pos = p.position || "UNK";
      if (!playersByPos[pos]) playersByPos[pos] = [];
      playersByPos[pos].push(p);
    }
    for (const pos of Object.keys(playersByPos)) {
      playersByPos[pos].sort((a, b) => b.value - a.value);
    }
  }

  const posOrder = ["QB", "RB", "WR", "TE"];

  return (
    <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 sm:py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 sm:mb-8 gap-3 sm:gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white uppercase" data-testid="league-name">
            {league.name}
          </h1>
          <div className="flex items-center gap-2 sm:gap-3 mt-1 flex-wrap">
            <span className="text-xs font-mono text-[#a3a3a3]">
              {league.settings.is_superflex ? 'SUPERFLEX' : '1QB'} / {league.settings.ppr} PPR / {league.settings.num_teams} TEAMS
            </span>
            <span className="text-xs font-mono text-[#666]">Season {league.season}</span>
            {league.scoring_adjustments?.has_adjustments && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#3b82f6]/10 text-[#3b82f6] border border-[#3b82f6]/30 rounded-sm" data-testid="scoring-adjusted-badge">
                SCORING ADJUSTED
              </span>
            )}
          </div>
          {league.scoring_adjustments?.active?.length > 0 && (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {league.scoring_adjustments.active.map((adj, i) => (
                <span key={i} className="text-[10px] font-mono text-[#3b82f6]">{adj}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(myRosterId || "")} onValueChange={(v) => setMyRosterId(Number(v))}>
            <SelectTrigger className="w-[220px] h-9 bg-[#171717] border-[#262626] text-sm text-white" data-testid="my-team-selector">
              <SelectValue placeholder="Select your team" />
            </SelectTrigger>
            <SelectContent className="bg-[#171717] border-[#262626]">
              {rosters.map((r) => (
                <SelectItem key={r.roster_id} value={String(r.roster_id)} className="text-white hover:bg-[#262626]">
                  {r.owner_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => navigate("/trade")}
            className="h-9 px-4 btn-primary rounded-sm text-xs"
            data-testid="go-trade-btn"
          >
            <ArrowsLeftRight size={14} weight="bold" className="mr-1" />
            TRADE
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-4 mb-6 sm:mb-8">
        <div className="border border-[#262626] bg-[#171717] p-4" data-testid="stat-total-value">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#a3a3a3] mb-1">TOTAL VALUE</p>
          <p className="font-mono font-bold text-2xl text-[#ccff00]">{myRoster?.total_value?.toLocaleString() || 0}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] font-mono text-[#666]">Players: {(myRoster?.player_value || 0).toLocaleString()}</span>
            <span className="text-[10px] font-mono text-[#666]">Picks: {(myRoster?.picks_value || 0).toLocaleString()}</span>
          </div>
        </div>
        <div className="border border-[#262626] bg-[#171717] p-4" data-testid="stat-rank">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#a3a3a3] mb-1">POWER RANK</p>
          <p className="font-mono font-bold text-2xl text-white">
            #{rosters.findIndex(r => r.roster_id === myRosterId) + 1}
            <span className="text-sm text-[#666] ml-1">/ {rosters.length}</span>
          </p>
        </div>
        <div className="border border-[#262626] bg-[#171717] p-4" data-testid="stat-roster-size">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#a3a3a3] mb-1">ROSTER SIZE</p>
          <p className="font-mono font-bold text-2xl text-white">{myRoster?.players?.length || 0}</p>
        </div>
        <div className="border border-[#262626] bg-[#171717] p-4" data-testid="stat-picks-value">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#a3a3a3] mb-1">DRAFT CAPITAL</p>
          <p className="font-mono font-bold text-2xl text-[#ccff00]">{(myRoster?.picks_value || 0).toLocaleString()}</p>
          <p className="text-[10px] font-mono text-[#666] mt-1">{(myRoster?.picks || []).filter(p => p.value > 0).length} valued picks</p>
        </div>
        <div className="border border-[#262626] bg-[#171717] p-4" data-testid="stat-top-asset">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#a3a3a3] mb-1">TOP ASSET</p>
          {(() => {
            const allAssets = [...(myRoster?.players || []), ...(myRoster?.picks || []).map(p => ({ ...p, position: "PICK" }))];
            const top = allAssets.sort((a, b) => (b.value || 0) - (a.value || 0))[0];
            return top ? (
              <div className="flex items-center gap-2">
                <PlayerPhoto playerId={top.player_id} name={top.name} position={top.position} size={36} />
                <div className="min-w-0">
                  <p className="font-bold text-base text-white truncate">{top.name}</p>
                  <p className="font-mono text-xs text-[#ccff00]">{(top.value || 0).toLocaleString()}</p>
                </div>
              </div>
            ) : <p className="text-[#666]">-</p>;
          })()}
        </div>
      </div>

      {/* Championship Score */}
      {champScore && champScore.score > 0 && (
        <div className="border border-[#262626] bg-[#171717] p-5 mb-6 sm:mb-8" data-testid="championship-score">
          <div className="flex flex-col sm:flex-row sm:items-start gap-5">
            {/* Score Circle */}
            <div className="text-center sm:text-left shrink-0">
              <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full border-4 ${champScore.score >= 70 ? 'border-[#4ade80]' : champScore.score >= 50 ? 'border-[#fbbf24]' : 'border-[#ff3b30]'}`}>
                <div>
                  <p className="font-mono text-2xl font-bold text-white leading-none">{champScore.score}</p>
                  <p className="text-[9px] font-bold text-[#888]">{champScore.grade}</p>
                </div>
              </div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#555] mt-2">CHAMP SCORE</p>
            </div>

            {/* Breakdown */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3">
                <Trophy size={16} className="text-[#ccff00]" weight="fill" />
                <h3 className="text-xs font-bold text-white uppercase">CHAMPIONSHIP BLUEPRINT SCORE</h3>
                <Link to="/championship-blueprint" className="text-[9px] text-[#555] hover:text-[#ccff00] no-underline ml-auto">What's this?</Link>
              </div>

              <div className="grid grid-cols-5 gap-2 mb-3">
                {[
                  { label: "Value", val: champScore.breakdown.value, max: 20, color: "#ccff00" },
                  { label: "QB", val: champScore.breakdown.qb, max: 20, color: "#ff6b6b" },
                  { label: "RB", val: champScore.breakdown.rb, max: 20, color: "#4ecdc4" },
                  { label: "WR", val: champScore.breakdown.wr, max: 20, color: "#45b7d1" },
                  { label: "TE/Conc", val: champScore.breakdown.te_concentration, max: 20, color: "#f9ca24" },
                ].map(b => (
                  <div key={b.label}>
                    <p className="text-[8px] font-bold uppercase text-[#555] mb-0.5">{b.label}</p>
                    <div className="h-3 bg-[#0a0a0a] rounded-sm overflow-hidden">
                      <div className="h-full rounded-sm" style={{ width: `${(b.val / b.max) * 100}%`, backgroundColor: b.color }} />
                    </div>
                    <p className="text-[9px] font-mono text-[#888] mt-0.5">{b.val}/{b.max}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
                {(champScore.checks || []).map((c, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    {c.met ? <Check size={12} className="text-[#4ade80] shrink-0" weight="bold" /> : <X size={12} className="text-[#ff3b30] shrink-0" weight="bold" />}
                    <span className={`text-[10px] ${c.met ? 'text-[#a3a3a3]' : 'text-[#888]'}`}>{c.label}</span>
                  </div>
                ))}
              </div>

              {champScore.champ_players?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#262626]">
                  <p className="text-[9px] font-bold uppercase text-[#555] mb-1">CHAMPIONSHIP PLAYERS ON YOUR ROSTER</p>
                  <div className="flex flex-wrap gap-2">
                    {champScore.champ_players.map((p, i) => (
                      <span key={i} className="text-[10px] px-2 py-0.5 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] rounded-sm">{p.name} <span className="text-[#888]">{p.pct}%</span></span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
        {/* Position Value Breakdown */}
        <div className="border border-[#262626] bg-[#171717] p-5" data-testid="chart-pos-breakdown">
          <h3 className="font-heading text-base text-white uppercase tracking-tight mb-4">POSITIONAL VALUE BREAKDOWN</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={(() => {
              const posColors = { QB: '#ff6b6b', RB: '#4ecdc4', WR: '#45b7d1', TE: '#f9ca24', PICKS: '#ccff00' };
              const rows = ["QB", "RB", "WR", "TE"].map(pos => {
                const posPlayers = (myRoster?.players || []).filter(p => p.position === pos);
                const total = posPlayers.reduce((s, p) => s + p.value, 0);
                return { pos, value: total, fill: posColors[pos] };
              });
              const picksTotal = (myRoster?.picks || []).reduce((s, p) => s + (p.value || 0), 0);
              rows.push({ pos: "PICKS", value: picksTotal, fill: posColors.PICKS });
              return rows;
            })()} layout="vertical" margin={{ left: 10, right: 20, top: 0, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="pos" width={42} tick={{ fill: '#a3a3a3', fontSize: 12, fontFamily: 'Bebas Neue' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: '#171717', border: '1px solid #262626', borderRadius: 0, fontFamily: 'JetBrains Mono', fontSize: 12, color: '#fff' }}
                labelStyle={{ color: '#fff', fontFamily: 'Bebas Neue' }}
                itemStyle={{ color: '#ccff00' }}
                formatter={(v) => [v.toLocaleString(), 'Value']}
              />
              <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                {["QB", "RB", "WR", "TE", "PICKS"].map((pos, i) => (
                  <Cell key={pos} fill={['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#ccff00'][i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* League Value Comparison */}
        <div className="border border-[#262626] bg-[#171717] p-5" data-testid="chart-league-comparison">
          <h3 className="font-heading text-base text-white uppercase tracking-tight mb-4">LEAGUE VALUE COMPARISON</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={rosters.slice(0, 10).map(r => ({
              name: r.owner_name.length > 10 ? r.owner_name.slice(0, 10) + '..' : r.owner_name,
              value: r.total_value,
              isMine: r.roster_id === myRosterId,
            }))} margin={{ left: 0, right: 10, top: 0, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fill: '#a3a3a3', fontSize: 9, fontFamily: 'Manrope' }} axisLine={false} tickLine={false} interval={0} angle={-25} textAnchor="end" height={50} />
              <YAxis hide />
              <Tooltip
                contentStyle={{ background: '#171717', border: '1px solid #262626', borderRadius: 0, fontFamily: 'JetBrains Mono', fontSize: 12, color: '#fff' }}
                labelStyle={{ color: '#fff' }}
                itemStyle={{ color: '#ccff00' }}
                formatter={(v) => [v.toLocaleString(), 'Total Value']}
              />
              <Bar dataKey="value" radius={[2, 2, 0, 0]} label={{ position: 'top', fill: '#a3a3a3', fontSize: 9, fontFamily: 'JetBrains Mono', formatter: (v) => v >= 1000 ? `${Math.round(v/1000)}k` : v }}>
                {rosters.slice(0, 10).map((r) => (
                  <Cell key={r.roster_id} fill={r.roster_id === myRosterId ? '#ccff00' : '#4a4a4a'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* My Roster */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="roster" className="w-full">
            <TabsList className="bg-[#171717] border border-[#262626] rounded-sm mb-4">
              <TabsTrigger value="roster" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-[#ccff00] data-[state=active]:text-black" data-testid="tab-roster">
                MY ROSTER
              </TabsTrigger>
              <TabsTrigger value="picks" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-[#ccff00] data-[state=active]:text-black" data-testid="tab-picks">
                PICK VALUES
              </TabsTrigger>
              <TabsTrigger value="top-assets" className="text-xs uppercase tracking-wider rounded-sm data-[state=active]:bg-[#ccff00] data-[state=active]:text-black" data-testid="tab-top-assets">
                TOP ASSETS
              </TabsTrigger>
            </TabsList>

            <TabsContent value="roster">
              <div className="border border-[#262626] bg-[#171717]" data-testid="roster-table">
                {posOrder.map((pos) => {
                  const players = playersByPos[pos] || [];
                  if (players.length === 0) return null;
                  const isExpanded = expandedPos === pos || expandedPos === null;
                  const displayPlayers = isExpanded ? players : players.slice(0, 3);
                  return (
                    <div key={pos}>
                      <button
                        onClick={() => setExpandedPos(expandedPos === pos ? null : pos)}
                        className="w-full flex items-center justify-between px-4 py-2 bg-[#0a0a0a] border-b border-[#262626] cursor-pointer hover:bg-[#171717] transition-colors"
                        data-testid={`pos-header-${pos}`}
                      >
                        <div className="flex items-center gap-2">
                          <PosBadge pos={pos} />
                          <span className="text-xs font-semibold text-[#a3a3a3] uppercase">{players.length} players</span>
                        </div>
                        {expandedPos === pos ? <CaretUp size={14} className="text-[#666]" /> : <CaretDown size={14} className="text-[#666]" />}
                      </button>
                      {displayPlayers.map((p, idx) => (
                        <div
                          key={p.player_id}
                          className="player-row flex items-center justify-between px-4 py-2.5 border-b border-[#1a1a1a] cursor-pointer"
                          style={{ animationDelay: `${idx * 30}ms` }}
                          data-testid={`player-row-${p.player_id}`}
                          onClick={() => setDetailPlayerId(p.player_id)}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-xs font-mono text-[#666] w-6 text-right">{idx + 1}</span>
                            <PlayerPhoto playerId={p.player_id} name={p.name} position={p.position} size={28} />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-white truncate">{p.name}</p>
                              <p className="text-[10px] font-mono text-[#666]">{p.team} {p.age ? `/ ${p.age}y` : ''}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <TrendIndicator value={p.trend} />
                            <ValueDisplay value={p.value} />
                          </div>
                        </div>
                      ))}
                      {!isExpanded && players.length > 3 && (
                        <button
                          onClick={() => setExpandedPos(pos)}
                          className="w-full py-1.5 text-[10px] font-semibold text-[#ccff00] uppercase tracking-wider hover:bg-[#1a1a1a] transition-colors"
                        >
                          Show {players.length - 3} more
                        </button>
                      )}
                    </div>
                  );
                })}
                {/* Draft Picks Section */}
                {(() => {
                  const valuedPicks = (myRoster?.picks || []).filter(p => p.value > 0);
                  if (valuedPicks.length === 0) return null;
                  const picksExpanded = expandedPos === "PICKS" || expandedPos === null;
                  const displayPicks = picksExpanded ? valuedPicks : valuedPicks.slice(0, 3);
                  return (
                    <div>
                      <button
                        onClick={() => setExpandedPos(expandedPos === "PICKS" ? null : "PICKS")}
                        className="w-full flex items-center justify-between px-4 py-2 bg-[#0a0a0a] border-b border-[#262626] cursor-pointer hover:bg-[#171717] transition-colors"
                        data-testid="pos-header-PICKS"
                      >
                        <div className="flex items-center gap-2">
                          <PosBadge pos="PICK" />
                          <span className="text-xs font-semibold text-[#a3a3a3] uppercase">{valuedPicks.length} picks</span>
                          <span className="font-mono text-[10px] text-[#666]">({valuedPicks.reduce((s, p) => s + p.value, 0).toLocaleString()} total)</span>
                        </div>
                        {expandedPos === "PICKS" ? <CaretUp size={14} className="text-[#666]" /> : <CaretDown size={14} className="text-[#666]" />}
                      </button>
                      {displayPicks.map((p, idx) => (
                        <div
                          key={`pick-${idx}`}
                          className="player-row flex items-center justify-between px-4 py-2.5 border-b border-[#1a1a1a]"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-xs font-mono text-[#666] w-6 text-right">{idx + 1}</span>
                            <PosBadge pos="PICK" />
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-white truncate">{p.name}</p>
                              <p className="text-[10px] font-mono text-[#666]">{p.is_own_pick ? 'Own pick' : 'Acquired'}</p>
                            </div>
                          </div>
                          <ValueDisplay value={p.value} />
                        </div>
                      ))}
                      {!picksExpanded && valuedPicks.length > 3 && (
                        <button
                          onClick={() => setExpandedPos("PICKS")}
                          className="w-full py-1.5 text-[10px] font-semibold text-[#ccff00] uppercase tracking-wider hover:bg-[#1a1a1a] transition-colors"
                        >
                          Show {valuedPicks.length - 3} more
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            </TabsContent>

            <TabsContent value="picks">
              <div className="border border-[#262626] bg-[#171717]" data-testid="picks-table">
                {/* My Picks */}
                <div className="px-4 py-2 bg-[#ccff00]/10 border-b border-[#262626] flex items-center justify-between">
                  <span className="text-xs font-semibold text-[#ccff00] uppercase">MY DRAFT PICKS</span>
                  <span className="text-xs font-mono text-[#a3a3a3]">{(myRoster?.picks || []).filter(p => p.value > 0).length} valued / {myRoster?.picks_value?.toLocaleString() || 0} total</span>
                </div>
                {(myRoster?.picks || []).filter(p => p.value > 0).map((pv, idx) => (
                  <div key={`my-${idx}`} className="player-row flex items-center justify-between px-4 py-2.5 border-b border-[#1a1a1a] bg-[#ccff00]/5">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-mono text-[#666] w-6 text-right">{idx + 1}</span>
                      <PosBadge pos="PICK" />
                      <div>
                        <span className="text-sm font-semibold text-[#ccff00]">{pv.name}</span>
                        <span className="text-[10px] font-mono text-[#666] ml-2">{pv.is_own_pick ? 'Own' : 'Acquired'}</span>
                      </div>
                    </div>
                    <ValueDisplay value={pv.value} />
                  </div>
                ))}
                {/* League Reference Values */}
                <div className="px-4 py-2 bg-[#0a0a0a] border-b border-[#262626] flex items-center justify-between">
                  <span className="text-xs font-semibold text-[#a3a3a3] uppercase">LEAGUE PICK VALUES</span>
                  <span className="text-xs font-mono text-[#666]">{pick_values?.length || 0} picks</span>
                </div>
                <ScrollArea className="h-[40vh] sm:h-[400px]">
                  {pick_values?.map((pv, idx) => (
                    <div key={idx} className="player-row flex items-center justify-between px-4 py-2.5 border-b border-[#1a1a1a]">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-mono text-[#666] w-6 text-right">{idx + 1}</span>
                        <PosBadge pos="PICK" />
                        <span className="text-sm font-semibold text-white">{pv.name}</span>
                      </div>
                      <ValueDisplay value={pv.value} />
                    </div>
                  ))}
                </ScrollArea>
              </div>
            </TabsContent>

            <TabsContent value="top-assets">
              <div className="border border-[#262626] bg-[#171717] p-5" data-testid="top-assets-chart">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#a3a3a3] mb-4">TOP 15 ASSETS BY VALUE (PLAYERS + PICKS)</p>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={(() => {
                    const allAssets = [
                      ...(myRoster?.players || []).map(p => ({ name: p.name, value: p.value, position: p.position })),
                      ...(myRoster?.picks || []).filter(p => p.value > 0).map(p => ({ name: p.name, value: p.value, position: "PICK" })),
                    ].sort((a, b) => b.value - a.value).slice(0, 15);
                    return allAssets.map(p => ({
                      name: p.name.length > 14 ? p.name.slice(0, 14) + '..' : p.name,
                      value: p.value,
                      pos: p.position,
                    }));
                  })()} layout="vertical" margin={{ left: 5, right: 20, top: 0, bottom: 0 }}>
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fill: '#a3a3a3', fontSize: 11, fontFamily: 'Manrope' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ background: '#171717', border: '1px solid #262626', borderRadius: 0, fontFamily: 'JetBrains Mono', fontSize: 12, color: '#fff' }}
                      labelStyle={{ color: '#fff' }}
                      itemStyle={{ color: '#ccff00' }}
                      formatter={(v) => [v.toLocaleString(), 'Value']}
                    />
                    <Bar dataKey="value" radius={[0, 2, 2, 0]}>
                      {(() => {
                        const allAssets = [
                          ...(myRoster?.players || []).map(p => ({ position: p.position, value: p.value })),
                          ...(myRoster?.picks || []).filter(p => p.value > 0).map(p => ({ position: "PICK", value: p.value })),
                        ].sort((a, b) => b.value - a.value).slice(0, 15);
                        const colors = { QB: '#ff6b6b', RB: '#4ecdc4', WR: '#45b7d1', TE: '#f9ca24', PICK: '#ccff00' };
                        return allAssets.map((p, i) => <Cell key={i} fill={colors[p.position] || '#ccff00'} />);
                      })()}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* AI Analysis */}
          <div className="border border-[#262626] bg-[#171717] p-5" data-testid="ai-analysis-card">
            <div className="flex items-center gap-2 mb-4">
              <Lightning size={18} weight="bold" className="text-[#ccff00]" />
              <h3 className="font-heading text-lg text-white uppercase tracking-tight">AI ROSTER ANALYSIS</h3>
            </div>
            {rosterAnalysis ? (
              <>
                <FormatMarkdown text={rosterAnalysis} data-testid="ai-analysis-text" />
                <button onClick={analyzeRoster} disabled={analyzing} className="mt-4 text-[10px] text-[#888] hover:text-[#ccff00] transition-colors flex items-center gap-1">
                  {analyzing ? <><div className="w-2.5 h-2.5 border border-[#888] border-t-transparent rounded-full animate-spin" /> Analyzing...</> : "Re-analyze roster"}
                </button>
              </>
            ) : (
              <Button
                onClick={analyzeRoster}
                disabled={analyzing}
                className="w-full h-10 btn-primary rounded-sm text-xs"
                data-testid="analyze-roster-btn"
              >
                {analyzing ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Analyzing your roster strengths & weaknesses...
                  </div>
                ) : (
                  "ANALYZE MY ROSTER"
                )}
              </Button>
            )}
          </div>

          {/* Enhanced Power Rankings with Champion DNA */}
          <div className="border border-[#262626] bg-[#171717] p-5" data-testid="power-rankings">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg text-white uppercase tracking-tight">POWER RANKINGS</h3>
              {leagueScores && <span className="text-[9px] text-[#555] uppercase">+ CHAMP SCORE</span>}
            </div>
            <div className="space-y-2">
              {(leagueScores?.scores || rosters.slice(0, 8).map(r => ({roster_id: r.roster_id, owner_name: r.owner_name, total_value: r.total_value, score: null}))).slice(0, 12).map((r, idx) => {
                const isMe = r.roster_id === myRosterId;
                return (
                  <div
                    key={r.roster_id}
                    className={`flex items-center justify-between py-2 px-3 border border-transparent rounded-sm transition-colors ${isMe ? 'bg-[#ccff00]/10 border-[#ccff00]/30' : 'hover:bg-[#262626]'}`}
                    data-testid={`power-rank-${idx + 1}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-sm text-[#666] w-5">{idx + 1}</span>
                      {idx === 0 && <Crown size={14} weight="fill" className="text-[#fbbf24]" />}
                      <span className={`text-sm font-semibold ${isMe ? 'text-[#ccff00]' : 'text-white'}`}>
                        {r.owner_name}
                      </span>
                      {r.contender_tier && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase" style={{ color: r.contender_color, backgroundColor: `${r.contender_color}15` }}>{r.contender_tier}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {r.score != null && (
                        <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded-sm ${r.score >= 70 ? 'bg-[#4ade80]/10 text-[#4ade80]' : r.score >= 50 ? 'bg-[#fbbf24]/10 text-[#fbbf24]' : 'bg-[#ff3b30]/10 text-[#ff3b30]'}`}>{r.score} {r.grade}</span>
                      )}
                      <span className="font-mono text-xs text-[#a3a3a3]">{(r.total_value || 0).toLocaleString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Roster Age Alerts + Trade Suggestions */}
          {leagueScores && (() => {
            const myScore = leagueScores.scores?.find(s => s.roster_id === myRosterId);
            if (!myScore) return null;
            const hasAlerts = myScore.age_alerts?.length > 0 || myScore.suggestion;
            if (!hasAlerts) return null;
            return (
              <div className="border border-[#262626] bg-[#171717] p-5 mt-4" data-testid="roster-alerts">
                <h3 className="font-heading text-lg text-white uppercase tracking-tight mb-3">ROSTER ALERTS</h3>
                
                {/* Age Alerts */}
                {myScore.age_alerts?.map((alert, i) => (
                  <div key={i} className="flex items-start gap-2 mb-2 px-3 py-2 bg-[#fbbf24]/5 border border-[#fbbf24]/20 rounded-sm">
                    <TrendDown size={14} className="text-[#fbbf24] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs text-white font-semibold">Your {alert.position} room is aging</p>
                      <p className="text-[10px] text-[#888]">Average {alert.your_avg} yrs vs {alert.champ_avg} yrs on championship rosters (+{alert.delta} yrs older). Consider moving an aging {alert.position} for a younger asset.</p>
                    </div>
                  </div>
                ))}

                {/* Trade Suggestion */}
                {myScore.suggestion && (
                  <div className="flex items-start gap-2 px-3 py-2 bg-[#ccff00]/5 border border-[#ccff00]/20 rounded-sm">
                    <Lightning size={14} className="text-[#ccff00] shrink-0 mt-0.5" weight="fill" />
                    <div>
                      <p className="text-xs text-white font-semibold">Build Like a Champion</p>
                      <p className="text-[10px] text-[#888]">{myScore.suggestion}</p>
                      {myScore.gaps?.length > 0 && (
                        <div className="flex gap-1.5 mt-1">
                          {myScore.gaps.map(g => <span key={g} className="text-[9px] px-1.5 py-0.5 bg-[#ff3b30]/10 border border-[#ff3b30]/30 text-[#ff3b30] rounded-sm">Need: {g}</span>)}
                        </div>
                      )}
                      {myScore.strengths?.length > 0 && (
                        <div className="flex gap-1.5 mt-1">
                          {myScore.strengths.map(s => <span key={s} className="text-[9px] px-1.5 py-0.5 bg-[#4ade80]/10 border border-[#4ade80]/30 text-[#4ade80] rounded-sm">{s}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Trade Partners */}
          {leagueScores && (() => {
            const myScore = leagueScores.scores?.find(s => s.roster_id === myRosterId);
            const partners = myScore?.trade_partners;
            if (!partners?.length) return null;
            return (
              <div className="border border-[#262626] bg-[#171717] p-5 mt-4" data-testid="trade-partners">
                <div className="flex items-center gap-2 mb-3">
                  <ArrowsLeftRight size={16} weight="bold" className="text-[#ccff00]" />
                  <h3 className="font-heading text-lg text-white uppercase tracking-tight">IDEAL TRADE PARTNERS</h3>
                </div>
                <p className="text-[10px] text-[#555] mb-3">Teams whose needs match your strengths (and vice versa).</p>
                <div className="space-y-2">
                  {partners.map((p, i) => {
                    const partnerRoster = rosters.find(r => r.roster_id === parseInt(p.roster_id) || r.roster_id === p.roster_id);
                    return (
                    <button
                      key={i}
                      onClick={async () => {
                        if (!partnerRoster || !myRoster) return;
                        setDeepDivePartner(p);
                        setDeepDiveLoading(true);
                        setDeepDiveSuggestions(null);
                        try {
                          const myScore = leagueScores.scores?.find(s => s.roster_id === myRosterId);
                          const theirScore = leagueScores.scores?.find(s => s.roster_id === parseInt(p.roster_id) || String(s.roster_id) === String(p.roster_id));
                          const resp = await axios.post(`${API}/trade/partner-suggestions`, {
                            my_players: myRoster.players || [],
                            their_players: partnerRoster?.players || [],
                            my_gaps: myScore?.gaps || [],
                            their_gaps: theirScore?.gaps || [],
                            partner_name: p.owner_name,
                          });
                          setDeepDiveSuggestions(resp.data);
                        } catch (e) { console.error(e); }
                        setDeepDiveLoading(false);
                      }}
                      className="w-full flex items-start gap-3 p-3 border border-[#262626] bg-[#0a0a0a] rounded-sm hover:border-[#ccff00]/30 transition-colors text-left"
                      data-testid={`trade-partner-${i}`}
                    >
                      <div className="shrink-0 mt-0.5">
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase" style={{ color: p.tier_color, backgroundColor: `${p.tier_color}15` }}>{p.tier}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-semibold">{p.owner_name}</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {p.they_fill?.map(g => (
                            <span key={`tf-${g}`} className="text-[9px] px-1.5 py-0.5 bg-[#4ecdc4]/10 border border-[#4ecdc4]/30 text-[#4ecdc4] rounded-sm">They have {g}</span>
                          ))}
                          {p.you_fill?.map(g => (
                            <span key={`yf-${g}`} className="text-[9px] px-1.5 py-0.5 bg-[#ccff00]/10 border border-[#ccff00]/30 text-[#ccff00] rounded-sm">You have {g}</span>
                          ))}
                        </div>
                        <p className="text-[9px] text-[#555] mt-1">Click for specific trade ideas</p>
                      </div>
                    </button>
                    );
                  })}
                </div>

                {/* Deep Dive: Trade Suggestions */}
                {deepDivePartner && (
                  <div className="mt-4 border border-[#ccff00]/20 bg-[#ccff00]/5 rounded-sm p-4" data-testid="trade-deep-dive">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xs font-bold text-white uppercase">TRADE IDEAS WITH {deepDivePartner.owner_name?.toUpperCase()}</h4>
                      <button onClick={() => { setDeepDivePartner(null); setDeepDiveSuggestions(null); }} className="text-[#666] hover:text-white"><X size={14} /></button>
                    </div>
                    {deepDiveLoading && <p className="text-[10px] text-[#888] animate-pulse">Finding trades...</p>}
                    {deepDiveSuggestions?.suggestions?.length === 0 && !deepDiveLoading && (
                      <p className="text-[10px] text-[#888]">No close-value matches found. Try the Trade Analyzer for AI-powered suggestions.</p>
                    )}
                    {deepDiveSuggestions?.suggestions?.map((s, idx) => (
                      <div key={idx} className="border border-[#262626] bg-[#171717] rounded-sm p-3 mb-2" data-testid={`deep-dive-trade-${idx}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${s.verdict === "FAIR" ? "bg-[#4ade80]/10 text-[#4ade80]" : s.verdict === "CLOSE" ? "bg-[#fbbf24]/10 text-[#fbbf24]" : "bg-[#ff3b30]/10 text-[#ff3b30]"}`}>{s.verdict}</span>
                          <span className="text-[9px] text-[#555] capitalize">{s.type} trade</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <p className="text-[9px] font-bold text-[#ff6b6b] uppercase mb-1">You Send</p>
                            {s.you_send?.map((p, j) => (
                              <div key={j} className="flex items-center gap-2 py-1">
                                <PlayerPhoto playerId={p.player_id} name={p.name} position={p.position} size={22} />
                                <div className="min-w-0">
                                  <p className="text-[11px] text-white font-semibold truncate">{p.name}</p>
                                  <p className="text-[9px] font-mono text-[#666]">{(p.value || 0).toLocaleString()}</p>
                                </div>
                              </div>
                            ))}
                            <p className="text-[9px] font-mono text-[#ff6b6b] mt-1">{(s.value_sent || 0).toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-[9px] font-bold text-[#4ade80] uppercase mb-1">You Get</p>
                            {s.you_receive?.map((p, j) => (
                              <div key={j} className="flex items-center gap-2 py-1">
                                <PlayerPhoto playerId={p.player_id} name={p.name} position={p.position} size={22} />
                                <div className="min-w-0">
                                  <p className="text-[11px] text-white font-semibold truncate">{p.name}</p>
                                  <p className="text-[9px] font-mono text-[#666]">{(p.value || 0).toLocaleString()}</p>
                                </div>
                              </div>
                            ))}
                            <p className="text-[9px] font-mono text-[#4ade80] mt-1">{(s.value_received || 0).toLocaleString()}</p>
                          </div>
                        </div>
                        <p className="text-[9px] text-[#888] mt-2">{s.reason}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Commissioner Dashboard */}
          {(user?.subscription === "commissioner" || user?.is_admin) && activeLeague && (
            <CommissionerDashboard leagueId={activeLeague.league_id} leagueName={activeLeague.name} />
          )}

          {/* Waiver Wire */}
          <div className="border border-[#262626] bg-[#171717] p-5" data-testid="waiver-wire-card">
            <div className="flex items-center gap-2 mb-4">
              <WifiHigh size={18} weight="bold" className="text-[#ccff00]" />
              <h3 className="font-heading text-lg text-white uppercase tracking-tight">WAIVER WIRE</h3>
            </div>
            {waiverSuggestions ? (
              <div className="space-y-2" data-testid="waiver-suggestions">
                {waiverSuggestions.map((w, idx) => (
                  <div key={idx} className="border border-[#262626] bg-[#0a0a0a] p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <PosBadge pos={w.position} />
                        <span className="text-sm font-semibold text-white">{w.name}</span>
                      </div>
                      <span className="font-mono text-xs text-[#ccff00]">{(w.value || 0).toLocaleString()}</span>
                    </div>
                    <p className="text-[10px] text-[#a3a3a3] leading-relaxed">{w.reason}</p>
                    {w.drop_candidate && w.drop_candidate !== "null" && (
                      <p className="text-[10px] text-[#ff3b30] mt-1">Drop: {w.drop_candidate}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Button
                onClick={async () => {
                  if (!myRoster) return;
                  setLoadingWaivers(true);
                  try {
                    const res = await axios.post(`${API}/waiver/suggest`, {
                      league_id: league.league_id,
                      my_roster_id: myRosterId,
                      my_roster: [myRoster],
                      all_rosters: rosters,
                      league_settings: league.settings,
                    });
                    setWaiverSuggestions(res.data.suggestions || []);
                  } catch (e) {
                    toast.error("Failed to get waiver suggestions");
                  } finally {
                    setLoadingWaivers(false);
                  }
                }}
                disabled={loadingWaivers}
                className="w-full h-10 btn-primary rounded-sm text-xs"
                data-testid="get-waivers-btn"
              >
                {loadingWaivers ? (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    Scanning waiver wire for your roster needs...
                  </div>
                ) : (
                  "SCAN WAIVER WIRE"
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      {detailPlayerId && (
        <PlayerDetailModal playerId={detailPlayerId} onClose={() => setDetailPlayerId(null)} />
      )}
    </div>
  );
}
